import { FilterState } from '../hooks/useFilterHistory';

/**
 * Storage service for persisting filter history
 * Supports local storage (primary) and optional backend sync
 */
export class FilterHistoryStorage {
  private readonly storageKey: string;
  private readonly userId?: string;
  
  constructor(dashboardId: string, userId?: string) {
    this.storageKey = `filter-history-${dashboardId}`;
    this.userId = userId;
  }
  
  /**
   * Save filter history to localStorage
   */
  saveToLocal(history: FilterState[]): void {
    try {
      const serialized = JSON.stringify(history.map(state => ({
        ...state,
        timestamp: state.timestamp.toISOString()
      })));
      
      localStorage.setItem(this.storageKey, serialized);
    } catch (error) {
      console.error('Failed to save filter history to localStorage:', error);
    }
  }
  
  /**
   * Load filter history from localStorage
   */
  loadFromLocal(): FilterState[] {
    try {
      const stored = localStorage.getItem(this.storageKey);
      
      if (!stored) {
        return [];
      }
      
      const parsed = JSON.parse(stored);
      
      // Convert timestamp strings back to Date objects
      return parsed.map((state: any) => ({
        ...state,
        timestamp: new Date(state.timestamp)
      }));
    } catch (error) {
      console.error('Failed to load filter history from localStorage:', error);
      return [];
    }
  }
  
  /**
   * Clear all stored history
   */
  clearStorage(): void {
    try {
      localStorage.removeItem(this.storageKey);
    } catch (error) {
      console.error('Failed to clear filter history:', error);
    }
  }
  
  /**
   * Sync filter history to backend (optional, for signed-in users)
   * This would integrate with your backend API
   */
  async syncToBackend(userId: string, history: FilterState[]): Promise<void> {
    try {
      // TODO: Implement backend API call when ready
      // Example:
      // await fetch('/api/analytics/filter-history', {
      //   method: 'POST',
      //   headers: {
      //     'Content-Type': 'application/json',
      //     'Authorization': `Bearer ${getAuthToken()}`
      //   },
      //   body: JSON.stringify({
      //     userId,
      //     dashboardId: this.storageKey,
      //     history: history.map(state => ({
      //       ...state,
      //       timestamp: state.timestamp.toISOString()
      //     }))
      //   })
      // });
      
      console.debug('Backend sync not yet implemented - using local storage only');
    } catch (error) {
      console.error('Failed to sync filter history to backend:', error);
      throw error;
    }
  }
  
  /**
   * Load filter history from backend (optional, for signed-in users)
   * This would integrate with your backend API
   */
  async loadFromBackend(userId: string): Promise<FilterState[]> {
    try {
      // TODO: Implement backend API call when ready
      // Example:
      // const response = await fetch(`/api/analytics/filter-history?userId=${userId}&dashboardId=${this.storageKey}`, {
      //   headers: {
      //     'Authorization': `Bearer ${getAuthToken()}`
      //   }
      // });
      // 
      // if (!response.ok) {
      //   throw new Error('Failed to load from backend');
      // }
      // 
      // const data = await response.json();
      // return data.history.map((state: any) => ({
      //   ...state,
      //   timestamp: new Date(state.timestamp)
      // }));
      
      console.debug('Backend sync not yet implemented - using local storage only');
      return this.loadFromLocal();
    } catch (error) {
      console.error('Failed to load filter history from backend:', error);
      // Fallback to local storage
      return this.loadFromLocal();
    }
  }
  
  /**
   * Merge local and backend history (conflict resolution)
   * Strategy: Last write wins, deduplicate by comparing filter values
   */
  mergeHistories(localHistory: FilterState[], backendHistory: FilterState[]): FilterState[] {
    const merged = [...localHistory, ...backendHistory];
    
    // Sort by timestamp (newest first)
    merged.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
    
    // Deduplicate based on filter values (keep most recent)
    const seen = new Set<string>();
    const deduplicated = merged.filter(state => {
      const key = JSON.stringify(state.filters);
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
    
    // Keep only the most recent 10
    return deduplicated.slice(0, 10);
  }
}


